"use client"

import { useState, useEffect, useRef } from "react"
import {
  BarChart2,
  Book,
  Play,
  Settings,
  Code,
  Users,
  Zap,
  ArrowRight,
  ChevronDown,
  ExternalLink,
  Bookmark,
} from "lucide-react"
import "../styles/HomePage.css"
import MainSidebar from "./MainSidebar"
import Button from "../../shared/Button"

const NUM_HERO_BARS = 26

const HomePage = () => {
  const [activeAlgorithm, setActiveAlgorithm] = useState("bubble")
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState("")
  const gettingStartedRef = useRef(null)
  const featuresRef = useRef(null)
  const learnMoreRef = useRef(null)
  const ctaSectionRef = useRef(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const [heroBars, setHeroBars] = useState([])
  const [comparingPair, setComparingPair] = useState([])
  const [sortedIndices, setSortedIndices] = useState(new Set())
  const sortTimerRef = useRef(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
      const scrollPosition = window.scrollY + 100

      if (gettingStartedRef.current && scrollPosition >= gettingStartedRef.current.offsetTop) {
        setActiveSection("getting-started")
      }
      if (featuresRef.current && scrollPosition >= featuresRef.current.offsetTop) {
        setActiveSection("features")
      }
      if (learnMoreRef.current && scrollPosition >= learnMoreRef.current.offsetTop) {
        setActiveSection("learn-more")
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToSection = (sectionRef) => {
    sectionRef.current.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    let arr = Array.from({ length: NUM_HERO_BARS }, () => Math.floor(Math.random() * 75) + 15)
    let i = 0
    let j = 0
    let sortedSet = new Set()

    setHeroBars([...arr])

    const tick = () => {
      if (i >= NUM_HERO_BARS - 1) {
        setSortedIndices(new Set(Array.from({ length: NUM_HERO_BARS }, (_, k) => k)))
        setComparingPair([])
        sortTimerRef.current = setTimeout(() => {
          arr = Array.from({ length: NUM_HERO_BARS }, () => Math.floor(Math.random() * 75) + 15)
          i = 0; j = 0; sortedSet = new Set()
          setSortedIndices(new Set())
          setHeroBars([...arr])
          sortTimerRef.current = setTimeout(tick, 300)
        }, 1400)
        return
      }

      setComparingPair([j, j + 1])

      if (arr[j] > arr[j + 1]) {
        ;[arr[j], arr[j + 1]] = [arr[j + 1], arr[j]]
        setHeroBars([...arr])
      }

      j++
      if (j >= NUM_HERO_BARS - i - 1) {
        sortedSet = new Set([...sortedSet, NUM_HERO_BARS - i - 1])
        setSortedIndices(new Set(sortedSet))
        i++
        j = 0
      }

      sortTimerRef.current = setTimeout(tick, 85)
    }

    sortTimerRef.current = setTimeout(tick, 600)
    return () => clearTimeout(sortTimerRef.current)
  }, [])

  return (
    <div className="home-page">
      <div className="hero">
        <div className="hero-bars" aria-hidden="true">
          {heroBars.map((height, idx) => (
            <div
              key={idx}
              className={`hero-bar${comparingPair.includes(idx) ? " comparing" : ""}${sortedIndices.has(idx) ? " sorted" : ""}`}
              style={{ height: `${height}%` }}
            />
          ))}
        </div>

        <div className="Sort">
          <h1 className="title">Visualize Data Structures & Algorithms</h1>
          <p className="subtitle">
            Explore the fascinating world of sorting algorithms through interactive visualizations and learn how data
            gets organized in computer science.
          </p>
          <a
            href="#cta-section"
            className="cta-button"
            onClick={(e) => {
              e.preventDefault()
              scrollToSection(ctaSectionRef)
            }}
          >
            Start Visualizing <ChevronDown className="icon-small" />
          </a>
        </div>

        <div className="hero-algo-label">bubble sort · live</div>
      </div>

      <div className="container">
        <div className="algorithm-preview" id="getting-started" ref={gettingStartedRef}>
          <h2 className="section-title">Algorithm Preview</h2>
          <div className="algorithm-tabs">
            <Button
              variant=""
              className={`tab ${activeAlgorithm === "bubble" ? "active" : ""}`}
              onClick={() => setActiveAlgorithm("bubble")}
            >
              Bubble Sort
            </Button>
            <Button
              variant=""
              className={`tab ${activeAlgorithm === "quick" ? "active" : ""}`}
              onClick={() => setActiveAlgorithm("quick")}
            >
              Quick Sort
            </Button>
            <Button
              variant=""
              className={`tab ${activeAlgorithm === "merge" ? "active" : ""}`}
              onClick={() => setActiveAlgorithm("merge")}
            >
              Merge Sort
            </Button>
          </div>
          <div className="bar-container">
            {[40, 60, 20, 80, 50, 30, 70, 10].map((height, index) => (
              <div key={index} className="bar" style={{ height: `${height}%` }}></div>
            ))}
          </div>
          <p className="algorithm-description">
            {activeAlgorithm === "bubble" &&
              "Bubble Sort repeatedly steps through the list, compares adjacent elements and swaps them if they are in the wrong order."}
            {activeAlgorithm === "quick" &&
              "Quick Sort picks an element as a pivot and partitions the given array around the picked pivot, recursively sorting the resulting sub-arrays."}
            {activeAlgorithm === "merge" &&
              "Merge Sort divides the unsorted list into n sublists, each containing one element, then repeatedly merges sublists to produce new sorted sublists."}
          </p>
        </div>

        <div className="getting-started">
          <h2 className="section-title">Getting Started</h2>
          <div className="getting-started-steps">
            <div className="step-card">
              <Play className="step-icon" />
              <h3 className="step-title">Choose Algorithm</h3>
              <p>Select from a variety of sorting algorithms to visualize.</p>
            </div>
            <div className="step-card">
              <Zap className="step-icon" />
              <h3 className="step-title">Adjust Parameters</h3>
              <p>Set the array size and sorting speed to your preference.</p>
            </div>
            <div className="step-card">
              <BarChart2 className="step-icon" />
              <h3 className="step-title">Visualize</h3>
              <p>Watch the sorting process unfold in real-time!</p>
            </div>
          </div>
        </div>

        <div className="section" id="features" ref={featuresRef}>
          <h2 className="section-title">Why Use Sorting Visualizer?</h2>
          <div className="feature-grid">
            <div className="feature">
              <Code className="Hfeature-icon" />
              <h3 className="feature-title">Learn Algorithms</h3>
              <p className="feature-description">
                Understand complex sorting algorithms through clear visual representations.
              </p>
            </div>
            <div className="feature">
              <Users className="Hfeature-icon" />
              <h3 className="feature-title">Compare Methods</h3>
              <p className="feature-description">See how different sorting algorithms perform side by side.</p>
            </div>
            <div className="feature">
              <Zap className="Hfeature-icon" />
              <h3 className="feature-title">Boost Understanding</h3>
              <p className="feature-description">
                Enhance your grasp of computer science concepts through interactive learning.
              </p>
            </div>
          </div>
        </div>

        {/* IMPROVED LEARN MORE SECTION */}
        <div className="learn-more-section" id="learn-more" ref={learnMoreRef}>
          <div className="learn-more-header">
            <h2 className="section-title">Expand Your Knowledge</h2>
            <p className="section-subtitle">
              Dive deeper into computer science concepts with our comprehensive resources
            </p>
          </div>

          <div className="knowledge-grid">
            <div className="knowledge-card featured">
              <div className="card-background-pattern"></div>
              <div className="card-content-wrapper">
                <div className="card-icon-wrapper">
                  <Book className="card-main-icon" />
                  <div className="icon-glow"></div>
                </div>
                <div className="card-header">
                  <h3 className="Hcard-title">DSA Notes</h3>
                  <span className="card-badge">Popular</span>
                </div>
                <p className="card-description">
                  Comprehensive notes covering data structures, algorithms, time complexity analysis, and
                  problem-solving techniques.
                </p>
                <div className="card-features">
                  <div className="feature-item">
                    <Bookmark className="Hfeature-icon" />
                    <span>15+ Topics Covered</span>
                  </div>
                  <div className="feature-item">
                    <Code className="Hfeature-icon" />
                    <span>Code Examples</span>
                  </div>
                  <div className="feature-item">
                    <BarChart2 className="Hfeature-icon" />
                    <span>Visual Diagrams</span>
                  </div>
                </div>
                <div className="card-footer">
                  <a href="/notes" className="primary-button">
                    <span>Explore DSA Notes</span>
                    <ArrowRight className="button-icon" />
                  </a>
                </div>
              </div>
            </div>

            <div className="knowledge-card">
              <div className="card-background-pattern secondary"></div>
              <div className="card-content-wrapper">
                <div className="card-icon-wrapper secondary">
                  <Settings className="card-main-icon" />
                  <div className="icon-glow secondary"></div>
                </div>
                <div className="card-header">
                  <h3 className="Hcard-title">Interactive Tutorials</h3>
                  <span className="card-badge secondary">New</span>
                </div>
                <p className="card-description">
                  Step-by-step interactive tutorials that guide you through algorithm implementation and optimization
                  techniques.
                </p>
                <div className="card-features">
                  <div className="feature-item">
                    <Play className="Hfeature-icon" />
                    <span>Hands-on Practice</span>
                  </div>
                  <div className="feature-item">
                    <Users className="Hfeature-icon" />
                    <span>Community Support</span>
                  </div>
                  <div className="feature-item">
                    <Zap className="Hfeature-icon" />
                    <span>Real-time Feedback</span>
                  </div>
                </div>
                <div className="card-footer">
                  <a
                    href="#"
                    className="secondary-button"
                    onClick={(e) => {
                      e.preventDefault()
                      setSidebarOpen(true)
                    }}
                  >
                    <span>Start Learning</span>
                    <ExternalLink className="button-icon" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="knowledge-stats">
            <div className="stat-item">
              <div className="stat-number">10K+</div>
              <div className="stat-label">Students Learning</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">50+</div>
              <div className="stat-label">Algorithms Covered</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">95%</div>
              <div className="stat-label">Success Rate</div>
            </div>
          </div> */}
        </div>

        <div className="cta-section" id="cta-section" ref={ctaSectionRef}>
          <h2 className="cta-title">Ready to Start Visualizing?</h2>
          <p className="cta-description">
            Join our community of learners and start exploring sorting algorithms today!
          </p>
          <a href="/visualization/sorting" className="large button pulse">
            Get Started <ArrowRight className="icon-small" />
          </a>
        </div>
      </div>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2025 Sorting Visualizer. All rights reserved.</p>
          <div className="footer-links">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
            <a href="/contact">Contact Us</a>
          </div>
        </div>
      </footer>
  
      {sidebarOpen && (
       
        <MainSidebar closeSidebar={() => setSidebarOpen(false)} />
      )}
    </div>
  )
}

export default HomePage
